<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter" title="Introduction">
<div class="titlepage">
<div>
<div>
<h1 class="title"><a id="d0e70" xmlns:saxon="http://icl.com/saxon"></a>Introduction</h1>
</div>
</div>
</div>
<p>As a new bride in the 1990s, uprooted from New York to another state, I had time to
                indulge my interest in cooking, long-held since childhood and cooking with my nanna.
                Relying very heavily on <span class="emphasis"><em>The Way to Cook,</em></span> by
                Julia Child, which had been a wedding present, I immersed myself into the wonderful
                world of (mostly) classical French cooking. I was quickly hooked, simmering, baking,
                saucing and roasting my way towards all kinds of new foods and pleasures. </p>
<p>The cooking itch soon became a passion, and that’s just about when all of the really
                lucky confluences started happening. I decided to put my college journalism major to
                work not in general writing, but in food writing in particular. My mother-in-law, a
                marvelous cook and an ardent gourmand, went along with my husband and me to my
                first-ever food and wine festival in Aspen, Colorado. It was there that I saw Julia
                Child, my childhood idol, performing a demonstration in which she tackled a rather
                large steamed lobster with a huge mallet. I summoned the nerve to approach Julia and
                ask what she thought I should do to get qualified as a food writer. Her graceful
                answer was actually more of a question: “Can you get to Le Cordon Bleu in Paris?” </p>
<p>I practically squealed <span class="emphasis"><em>oui!</em></span> As good fortune
                would have it, my husband supported me on this quest. Many years of having studied
                French and functioning as a sort of uninitiated Francophile practically carried me
                over the ocean to Paris. Upon landing, a sense of clarity and purpose hit me in a
                flash, even as I saw the little rabbits scurrying around the fields surrounding
                Charles de Gaulle airport. I was home, and it felt <span class="emphasis"><em>delicieux. </em></span></p>
<p>Since then, I’ve worked in many kitchens and traveled all over the world, but nothing
                has touched me like my French experience. </p>
<p>It’s an honor to be the author of this second volume in The French Cook series. </p>
<h3><a id="d0e99" xmlns:saxon="http://icl.com/saxon"></a>A Few Words on Choux Pastry </h3>
<p><span class="emphasis"><em>Mon petit choux</em></span> (my little cabbage) is a
                common term of endearment in France, not unlike “my darling” or “my dear” in
                English. Similarly, choux pastry is a much beloved and ultra-versatile pastry that
                is truly unlike any other. Where most pastries—such as a short pastry used in
                tarts—rely on minimal gluten activation to maximize crispiness and tenderness, choux
                pastry likes to get beat up a bit with a wooden spoon. This actually maximizes
                gluten and helps create the light, airy, moist interior and crispy exterior crunch
                for which choux is so celebrated. </p>
<p>Like many culinary wonders and other French classics such as the <span class="emphasis"><em>tarte tatin</em></span>, choux is thought to have
                originated as something of a mistake. The popular theory is that an errant pastry
                chef whipped up a pastry cream (usually a sweet pastry filling), forgot to add the
                sugar, and baked it, yielding something very similar to what we now know as choux. </p>
<p>Indeed, barring the sugar, the ingredients in a <span class="link" href="#/ch06#mastercream" title="Link target outside of Sample">pastry
                    cream</span> and choux pastry are quite similar. Choux begins by beating flour into
                warm water and melted butter to form a thick white dough. Eggs are then added
                gradually until a glossy, smooth, beautiful pastry dough is formed. The gluten in
                the flour provides the structure, the butter gives it flavor and depth, and the eggs
                encourage the glorious lift and puff of the choux. </p>
<p>It’s not a complicated pastry. In fact, at Le Cordon Bleu in Paris, where I first
                prepared it, choux was part of the beginning cuisine curriculum. We’ll go into more
                detail about preparing the pastry itself in The Art of Making Choux Pastry chapter,
                but for now, what matters is to contemplate the virtually endless possibilities for
                choux pastry. Because the slightly nutty, buttery flavor is neutral, the puffy
                shapes so pretty and appealing, and the texture both elegant and homey, choux can
                become the casing for many wonderful sweet or savory treats of all shapes and sizes. </p>
<p>Pâte à choux blended with grated cheese and possibly some fresh herbs becomes a warm,
                inviting cheese puff to have with a sip of Champagne before dinner. Or, filled with
                ham and cheese, the same puff can become a satisfying, elegant sandwich for lunch or
                to serve at cocktail parties. You can fill choux pastries with warm sautéed onions
                topped with cheese, a blend of cheese and smoked salmon and caviar, or roast beef
                and a horseradish cream—anything goes! <span class="emphasis"><em>Tout est possible
                    </em></span> (the sky is the limit). But don’t stop there. Add a bit of sugar to
                the flour and you have a slightly sweetened choux, the base for myriad sweet
                concoctions such as éclairs, St. Honoré, croquembouche, cream puffs and more filled
                with glorious butter creams, whipped creams, puddings, and ice creams paired with
                sauces and glazes of infinite varieties. Fried choux becomes a <span class="emphasis"><em>beignet</em></span>, or French doughnut. </p>
<p>This book is divided into two parts: savory and sweet. Like most meals, it begins
                with the savory chapter and ends with the sweet desserts chapter. Along the way,
                there will be all kinds of tips and beautiful photography to help make your choux
                adventure as enjoyable and delicious as can be. </p>
<p>
<span class="emphasis">
<em>Bon appetit!</em>
</span>
</p>
<div class="mediaobject" title="Photo of sugars and salt.">
<img alt="Photo of sugars and salt." src="images/Sugars-Salt_1282.jpg"/>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ch01.vue?579682b2